import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { signOut } from '../../lib/supabase';
import {
  GRANT_KINDS_WON,
  IDENTITIES,
  NICKNAME_MAX,
  NICKNAME_MIN,
  REGIONS,
  VISIT_REASONS,
  identityOf,
  identityRole,
  identityTagline,
  type GrantKindWonKey,
  type IdentityKey,
  type MeDto,
  type Region,
  type VisitReasonKey,
} from '@zoxo/shared';

import { LogOut } from '@/components/icons';
import { EditDialog } from '@/components/ui/edit-dialog';
import { Input } from '@/components/ui/input';
import { useSnackbar } from '@/components/ui/magic-snackbar';
import { PILL_TAB, PILL_TAB_OFF, PILL_TAB_ON } from '@/components/ui/pill-tab';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useUpdateMe } from '../auth/use-update-me';

/**
 * 프로필 — **읽는 화면.** 머리(이모지·이름·관심 지역) 아래에 줄 목록. 줄마다 오른쪽 「수정」이
 * 작은 팝업을 열어 그 값 하나만 고친다. 폼도 저장 버튼도 없다. 정체성 슬라이드 창(네 걸음)은
 * 첫 온보딩 자리(머리 아래 안내줄 · /onboarding)에서만 연다.
 */
type Editing = 'nickname' | 'regions' | 'identity' | 'reason' | 'wins' | null;
const WINS_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: '0회' }, { value: 1, label: '1회' }, { value: 2, label: '2회' },
  { value: 3, label: '3회' }, { value: 4, label: '4회' }, { value: 5, label: '5회 이상' },
];
// 팝업 안 알약 — 켜짐/꺼짐(온보딩 폼과 같은 꼴).
const CHIP_ON = cn(PILL_TAB, PILL_TAB_ON);
const CHIP_OFF = cn(PILL_TAB, PILL_TAB_OFF, 'border border-gray-200');
// 팝업 안 한 줄짜리 라디오 카드(정체성 · 방문 이유) — 온보딩 폼과 같은 꼴.
const CARD = 'rounded-xl border px-4 py-3 text-left transition-colors';
const CARD_ON = 'border-gray-900 bg-gray-900 text-white';
const CARD_OFF = 'border-gray-200 bg-white hover:bg-gray-50';
// 「수정」·「로그아웃」 모두 사이트 알약(pill-tab). 꺼진 알약에 테두리만 — 목록 줄 끝에서도
// 버튼으로 읽힌다. gap-1 은 아이콘이 붙는 「로그아웃」 알약을 위해.
const PILL = cn(PILL_TAB, PILL_TAB_OFF, 'gap-1 border border-gray-200 bg-white');

function Row({ label, children, onEdit, editLabel = '수정' }: { label: string; children: React.ReactNode; onEdit?: () => void; editLabel?: string }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <span className="w-20 shrink-0 text-xs text-text-subtle">{label}</span>
      <span className="min-w-0 flex-1 text-sm text-text">{children}</span>
      {onEdit ? (
        <button type="button" onClick={onEdit} className={PILL}>{editLabel}</button>
      ) : null}
    </div>
  );
}

export function ProfilePage({ me }: { me: MeDto }) {
  const update = useUpdateMe();
  const { showSnackbar } = useSnackbar();
  const [editing, setEditing] = useState<Editing>(null);
  const [nickname, setNickname] = useState(me.nickname);
  const [regions, setRegions] = useState<Region[]>(me.regions);
  const [identityKey, setIdentityKey] = useState<IdentityKey | null>(me.identity);
  const [reasonKey, setReasonKey] = useState<VisitReasonKey | null>(me.visitReason);
  const [winsDraft, setWinsDraft] = useState(me.grantWins ?? 0);
  const [kindKeys, setKindKeys] = useState<GrantKindWonKey[]>(me.grantKinds);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const logout = async () => {
    await signOut();
    queryClient.removeQueries({ queryKey: ['me'] });
    void navigate({ to: '/' });
  };

  const identity = identityOf(me.identity);
  const reason = VISIT_REASONS.find((r) => r.key === me.visitReason)?.label ?? null;
  const kinds = GRANT_KINDS_WON.filter((k) => me.grantKinds.includes(k.key)).map((k) => k.label);
  const wins = me.grantWins === null ? null : me.grantWins >= 5 ? '5회 이상' : `${me.grantWins}회`;

  const nick = nickname.trim();
  const nickOk = nick.length >= NICKNAME_MIN && nick.length <= NICKNAME_MAX;
  const openNickname = () => { setNickname(me.nickname); setEditing('nickname'); };
  const openRegions = () => { setRegions(me.regions); setEditing('regions'); };
  const openIdentity = () => { setIdentityKey(me.identity); setEditing('identity'); };
  const openReason = () => { setReasonKey(me.visitReason); setEditing('reason'); };
  const openWins = () => { setWinsDraft(me.grantWins ?? 0); setKindKeys(me.grantKinds); setEditing('wins'); };
  const close = (o: boolean) => { if (!o) { setEditing(null); update.reset(); } };
  const save = (input: Parameters<typeof update.mutate>[0], done: string) =>
    update.mutate(input, {
      onSuccess: () => { setEditing(null); showSnackbar(done, { type: 'success' }); },
    });
  // ApiError 는 `status` 를 든다(statusCode 아님). 닉네임 UNIQUE 충돌은 409.
  const errorText = update.isError
    ? update.error instanceof ApiError && update.error.status === 409
      ? '이미 사용중인 닉네임이다.'
      : '저장하지 못했다. 잠시 후 다시 시도하자.'
    : null;

  return (
    <div>
      {/* 머리: 지금 남에게 보이는 모습 */}
      <div className="flex items-center gap-4 py-2">
        {/* 이모지·역할에 마우스를 올리면 설명 「스스로 꽁돈을 찾는 자.」 툴팁. */}
        <div
          className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-muted"
          title={identity ? identityTagline(identity) : undefined}
        >
          {me.avatar ? (
            <img src={me.avatar} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-3xl" role="img" aria-label={identity ? `${identityRole(identity)}: ${identityTagline(identity)}` : '병아리'}>
              {identity?.emoji ?? '🐣'}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-base font-bold text-text">{me.nickname}</p>
          {/* 관심 지역 — 「서울 · 경기」. 안 골랐으면 전국. 정체성은 아래 상자에서 본다. */}
          <p className="text-sm text-text-muted">
            {me.regions.length > 0 ? me.regions.join(' · ') : '전국'}
          </p>
        </div>
        {/* 로그아웃 — 이름과 같은 줄 맨 오른쪽. ml-auto 가 이름 칸과 사이를 밀어낸다.
            알약 + size-3.5 아이콘 — 아티클의 수정·삭제 알약과 같은 꼴. */}
        <button type="button" onClick={() => void logout()} className={cn(PILL, 'ml-auto')}>
          <LogOut className="size-3.5" aria-hidden="true" />
          로그아웃
        </button>
      </div>
      <div className="mt-4 divide-y divide-border-subtle border-y border-border-subtle">
        <Row label="닉네임" onEdit={openNickname}>{me.nickname}</Row>
        <Row label="관심 지역" onEdit={openRegions}>
          {me.regions.length > 0 ? me.regions.join(' · ') : <span className="text-text-muted">전국</span>}
        </Row>
        {/* 역할 · 설명 전체 — 「🐗 창업가 · 창업에 돌진하는 자.」 */}
        <Row label="정체성" onEdit={openIdentity}>
          {identity ? `${identity.emoji} ${identityRole(identity)} · ${identityTagline(identity)}` : '—'}
        </Row>
        <Row label="방문 이유" onEdit={openReason}>{reason ?? '—'}</Row>
        <Row label="누적 꽁돈" onEdit={openWins}>
          {wins ?? '—'}
          {kinds.length > 0 ? ` · ${kinds.join(' · ')}` : ''}
        </Row>
        {me.role === 'admin' ? (
          <Row label="관리">
            <Link to="/admin/articles" className="text-sm underline underline-offset-2 hover:text-text">기사 승인 큐 →</Link>
          </Row>
        ) : null}
      </div>

      <EditDialog
        open={editing === 'nickname'}
        onOpenChange={close}
        title="닉네임"
        description={`최소 ${NICKNAME_MIN}자 ~ 최대 ${NICKNAME_MAX}자`}
        canSave={nickOk && nick !== me.nickname}
        saving={update.isPending}
        error={errorText}
        onSave={() => save({ nickname: nick }, '닉네임을 바꿨어요.')}
      >
        <Input value={nickname} onChange={(e) => setNickname(e.target.value)} maxLength={NICKNAME_MAX} autoFocus autoComplete="nickname" />
      </EditDialog>

      <EditDialog
        open={editing === 'regions'}
        onOpenChange={close}
        title="관심 지역"
        description="여러 개 골라도 돼요. 아무것도 안 고르면 전국이에요."
        saving={update.isPending}
        error={errorText}
        onSave={() => save({ regions }, '관심 지역을 저장했어요.')}
      >
        <div role="group" aria-label="관심 지역" className="flex flex-wrap gap-1">
          {REGIONS.map((r) => {
            const on = regions.includes(r);
            return (
              <button
                key={r}
                type="button"
                aria-pressed={on}
                onClick={() => setRegions((cur) => (on ? cur.filter((x) => x !== r) : [...cur, r]))}
                className={cn(PILL_TAB, on ? PILL_TAB_ON : cn(PILL_TAB_OFF, 'border border-gray-200'))}
              >
                {r}
              </button>
            );
          })}
        </div>
      </EditDialog>

      {/* 정체성 — 하나만. 자동 이름(「🐣병아리4821」)이면 서버가 새 정체성으로 다시 짓는다. */}
      <EditDialog
        open={editing === 'identity'}
        onOpenChange={close}
        title="정체성"
        description="나를 설명하는 가장 적절한 identity 하나."
        canSave={identityKey !== null && identityKey !== me.identity}
        saving={update.isPending}
        error={errorText}
        onSave={() => { if (identityKey) save({ identity: identityKey }, '정체성을 바꿨다.'); }}
      >
        <div role="radiogroup" aria-label="정체성" className="grid max-h-[60dvh] gap-2 overflow-y-auto">
          {IDENTITIES.map((it) => {
            const on = identityKey === it.key;
            return (
              <button
                key={it.key}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setIdentityKey(it.key)}
                className={cn(CARD, 'flex items-center gap-3', on ? CARD_ON : CARD_OFF)}
              >
                <span className="text-2xl" aria-hidden>{it.emoji}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold">{it.label}</span>
                  <span className={cn('block text-xs', on ? 'text-gray-300' : 'text-text-muted')}>{it.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
      </EditDialog>

      <EditDialog
        open={editing === 'reason'}
        onOpenChange={close}
        title="방문 이유"
        description="방문 이유를 하나 선택한다면?"
        canSave={reasonKey !== null && reasonKey !== me.visitReason}
        saving={update.isPending}
        error={errorText}
        onSave={() => { if (reasonKey) save({ visitReason: reasonKey }, '방문 이유를 바꿨다.'); }}
      >
        <div role="radiogroup" aria-label="방문 이유" className="grid max-h-[60dvh] gap-2 overflow-y-auto">
          {VISIT_REASONS.map((r) => {
            const on = reasonKey === r.key;
            return (
              <button
                key={r.key}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setReasonKey(r.key)}
                className={cn(CARD, 'text-sm', on ? cn(CARD_ON, 'font-medium') : CARD_OFF)}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </EditDialog>

      {/* 누적 꽁돈 — 횟수 + (1회 이상이면) 종류. 0회면 종류는 비운다. */}
      <EditDialog
        open={editing === 'wins'}
        onOpenChange={close}
        title="누적 꽁돈"
        description="내가 발표/질의응답 세션에 참여후 선정된 경우만 센다."
        canSave={winsDraft === 0 || kindKeys.length > 0}
        saving={update.isPending}
        error={errorText}
        onSave={() => save({ grantWins: winsDraft, grantKinds: winsDraft > 0 ? kindKeys : [] }, '누적 꽁돈을 바꿨다.')}
      >
        <div role="radiogroup" aria-label="따먹은 횟수" className="flex flex-wrap gap-1.5">
          {WINS_OPTIONS.map((o) => {
            const on = winsDraft === o.value;
            return (
              <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => setWinsDraft(o.value)} className={on ? CHIP_ON : CHIP_OFF}>
                {o.label}
              </button>
            );
          })}
        </div>
        {winsDraft > 0 ? (
          <>
            <p className="mt-4 text-xs text-text-muted">어떤 걸 따먹어 봤나? 여러 개 골라도 된다.</p>
            <div role="group" aria-label="따먹은 종류" className="mt-2 flex flex-wrap gap-1.5">
              {GRANT_KINDS_WON.map((k) => {
                const on = kindKeys.includes(k.key);
                return (
                  <button
                    key={k.key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setKindKeys((cur) => (on ? cur.filter((x) => x !== k.key) : [...cur, k.key]))}
                    className={on ? CHIP_ON : CHIP_OFF}
                  >
                    {on ? '☑ ' : '☐ '}{k.label}
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </EditDialog>
    </div>
  );
}
